/**
 * safeJson — robustly parse a fetch Response body as JSON, even when the
 * backend returns empty bodies, HTML error pages (Cloudflare 5xx, ingress
 * timeout pages), or partially-read streams.
 *
 * Why this exists: native `await res.json()` throws "Failed to execute 'json' on
 * 'Response': body stream already read" if the body has been consumed earlier
 * in the chain, or "Unexpected token '<'..." if the body is HTML. Both errors
 * are confusing to end-users and obscure the real status code.
 *
 * Always returns a plain object (never throws), so callers can do:
 *   const data = await safeJson(res);
 *   if (!res.ok) throw new Error(data.detail || data.message || `HTTP ${res.status}`);
 *
 * Reads body via `res.text()` once, then JSON.parses, so the body stream is
 * only ever read in a single place.
 */
export async function safeJson(res) {
  let text = '';
  try {
    if (res.bodyUsed) return {};
    text = await res.text();
  } catch {
    return {};
  }
  if (!text || !text.trim()) return {};
  try {
    return JSON.parse(text);
  } catch {
    // Backend returned HTML/plain text (e.g. Cloudflare 520, nginx timeout). Surface
    // a short snippet as `detail` so the caller can show a sensible toast.
    const snippet = text.replace(/<[^>]*>/g, '').trim().slice(0, 140);
    return { detail: snippet || `Server returned non-JSON (HTTP ${res.status})` };
  }
}
